import { apiBaseUrl } from './config.js';
import { getAuthToken } from './auth.js';
import { socket } from './socketU.js';
import { t, confirmT, updateElementText } from './translationHelper.js';
import { showMoodAnalysisPopup } from './mood.js';
import { showNotification } from './notif.js';

// Redirect guard: only forward from stockTransaction.html
window.isOn ||= (file) =>
  location.pathname.endsWith(`/${file}`) || location.pathname.endsWith(file);

// Where this TAB calls home. zfloor.js stamps sessionStorage.zxHome on every
// load, so any classic page opened from a tab that has seen the floor knows to
// go back to the floor — even down a path that never recorded an explicit
// zxReturnTo. That is the whole point: zxReturnTo is written by ONE function at
// hand-off time and is CONSUMED by the first reader, so any route that skips it
// (or loses the race) fell through to a hardcoded 'trader.html'/'market.html'
// and dumped a floor user into the classic system. This is the backstop under
// all of them. sessionStorage, not localStorage: it dies with the tab, so a
// market-origin session in a fresh tab is never affected.
window.zxHome ||= () => {
  try { return sessionStorage.getItem('zxHome'); } catch (_) { return null; }
};

// The 3D floor (zfloor.html) is the primary interface. It records zxReturnTo
// before handing off here, so a finished bid or swap lands back on the floor
// instead of the classic market page. Market-origin trades are unaffected.
window.zxTakeReturn ||= () => {
  const target = localStorage.getItem('zxReturnTo');
  // Default is the FLOOR, never a classic page: mobile browsers drop
  // sessionStorage when they discard/restore a tab, so zxHome can vanish
  // mid-session — falling through to market.html stranded users in the
  // abandoned classic system.
  if (!target) return window.zxHome?.() || 'zfloor.html';
  localStorage.removeItem('zxReturnTo');
  return target;
};

window.shouldRedirectToMarket ||= () =>
  window.isOn('stockTransaction.html') ||
  (window.isOn('transaction.html') && !!localStorage.getItem('zxReturnTo'));

window.doMarketRedirectIfNeeded ||= () => {
  // Only redirect if on a transaction page AND user is an initiator (not a responder)
  if (window.shouldRedirectToMarket() && window.__userIsInitiator !== false) {
    window.location.href = window.zxTakeReturn();
  }
};


function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
}

function getPortfolioId() {
  return getCookie('portfolioId') || localStorage.getItem('portfolioId');
}

// Fetch and display stocks with authentication
export function fetchStocks() {
  const token = getAuthToken();  // Get the token

  if (!token) {
	showNotification("You must be logged in to perform this action.", false);
	return;
  }

  fetch(`${apiBaseUrl}/api/stocks`, {
	headers: {
	  'Authorization': `Bearer ${getAuthToken()}`,  // Add the token to the Authorization header
	  'Content-Type': 'application/json'
	}
  })
	.then(response => {
	  if (!response.ok) {
		throw new Error(`HTTP error! status: ${response.status}`);
	  }
	  return response.json();
	})
	.then(stocks => {
	  if (stocks && Array.isArray(stocks)) {
		displayStocks(stocks);
	  } else {
		console.error("Received stocks data is not an array:", stocks);
	  }
	})
	.catch(error => {
	  console.error('Error fetching stocks:', error);
	});
}

// stockTrade.js

export function showWaitingForResponsePopup(message, timeoutSeconds = 10) {
  let existingPopup = document.getElementById('waitingPopup');
  if (existingPopup) existingPopup.remove();

  const popup = document.createElement('div');
  popup.id = 'waitingPopup';
  popup.style.cssText = `
	position: fixed;
	inset: 0;
	background: rgba(0, 0, 0, 0.8);
	color: #00ff00;
	padding: env(safe-area-inset-top, 20px) env(safe-area-inset-right, 20px) env(safe-area-inset-bottom, 20px) env(safe-area-inset-left, 20px);
	z-index: 10000;
	font-family: 'bithigh', Arial, sans-serif;
	display: grid;
	place-items: center;
	text-align: center;
  `;

  const inner = document.createElement('div');
  inner.style.cssText = `max-width: 720px; width: 90%;`;

  const title = document.createElement('h3');
  title.textContent = message || t('stock_bid.processing_bid', 'Processing your bid...');
  title.style.marginBottom = '12px';
  inner.appendChild(title);

  const spinner = document.createElement('div');
  spinner.className = 'spinner';
  spinner.style.cssText = `
	border: 3px solid rgba(0, 255, 0, 0.3);
	border-top: 3px solid #00ff00;
	border-radius: 50%;
	width: 40px;
	height: 40px;
	animation: spin 1s linear infinite;
	margin: 16px auto 10px auto;
  `;
  inner.appendChild(spinner);

  const countdown = document.createElement('div');
  countdown.id = 'waitingCountdown';
  countdown.style.cssText = 'margin-top: 8px;';
  inner.appendChild(countdown);

  popup.appendChild(inner);

  const style = document.createElement('style');
  style.textContent = `
	@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
  `;
  document.head.appendChild(style);

  document.body.appendChild(popup);

  // Track popup state for bot interaction management
  if (window.popupTracker) {
    window.popupTracker.trackElement(popup, 'waiting-for-response');
  }

  let remaining = Math.max(1, Number(timeoutSeconds) || 10);
  countdown.textContent = t('stock_bid.time_remaining', 'Time remaining: {seconds} seconds').replace('{seconds}', `${remaining}`);

  const interval = setInterval(() => {
	remaining -= 1;
	if (remaining <= 0) {
	  clearInterval(interval);
	  countdown.textContent = t('stock_bid.no_response', 'No response received. This action has expired.');
	  showNotification(t('stock_bid.offer_expired', 'No response received. Your offer expired.'), false);
	  // Close popup and trigger redirect for initiator
	  try {
		closeWaitingForResponsePopup?.(false); // false = initiator, triggers redirect
	  } catch (_) {}
	} else {
	  countdown.textContent = t('stock_bid.time_remaining', 'Time remaining: {seconds} seconds').replace('{seconds}', `${remaining}`);
	}
  }, 1000);

  // Store interval ID so it can be cleared
  window.__waitingPopupInterval = interval;

  // Expose closer globally - REMOVED redirect logic
  window.closeWaitingForResponsePopup = (isResponder = false) => {
	clearInterval(interval);
	const p = document.getElementById('waitingPopup');
	if (p) {
	  // Untrack popup before removing
	  if (window.popupTracker) {
		window.popupTracker.unregisterPopup('waitingPopup');
	  }
	  p.remove();
	}
	console.log('[Popup] Closed, isResponder:', isResponder);
	// Do NOT redirect here - let the main closeWaitingForResponsePopup handle it
  };
}




export async function fetchStockData(stockId) {
  try {
	const response = await fetch(`${apiBaseUrl}/api/stocks/${stockId}`, {  // No comma here
	  headers: {
		'Authorization': `Bearer ${getAuthToken()}`
	  }
	});
	if (!response.ok) {
	  throw new Error(`Failed to fetch stock data: ${response.status} - ${response.statusText}`);
	}
	return await response.json();
  } catch (error) {
	console.error('Error fetching stock data:', error);
	return null;
  }
}


export function displayStocks(stocks) {
  const stockList = document.getElementById('stock-list');
  if (!stockList) {
	console.error("Stock list element not found.");
	return;
  }

  if (!Array.isArray(stocks)) {
	console.error("Invalid stocks data:", stocks);
	return;
  }

  stockList.innerHTML = ''; // Clear existing stocks

  stocks.forEach(stock => {
	const stockElement = document.createElement('div');
	stockElement.innerHTML = `
	  <h3>${stock.name}</h3>
	  <p>${stock.description}</p>
	  <p>Product: ${stock.product}</p>
	  <p>Available Shares: ${stock.availableShares}</p>
	  <p>Share Value: ${stock.shareValue}</p>
	  <p>Company Value: ${(stock.shareValue * stock.availableShares).toFixed(2)}</p>
	  <button class="trade-button" data-stock-id="${stock._id}">Trade</button>
	`;
	stockList.appendChild(stockElement);
	addGlobalIndicator(stockElement, stock);
	const tradeButton = stockElement.querySelector('.trade-button');
	tradeButton.onclick = () => {
	  openStockTradeModal(stock);
	};
  });
}

export function getSessionId() {
  return localStorage.getItem('sessionId') || null;
}

export function openStockTradeModal(stock) {
  const stockTradeModal = document.createElement('div');
  stockTradeModal.classList.add('modal');
  stockTradeModal.innerHTML = `
	<div class="modal-content">
	  <span class="close">&times;</span>
	  <h2>Trade Stock</h2>
	  <p><strong>Company:</strong> ${stock.name}</p>
	  <p><strong>Description:</strong> ${stock.description}</p>
	  <p><strong>Product:</strong> ${stock.product}</p>
	  <p><strong>Available Shares:</strong> ${stock.availableShares}</p>
	  <button id="bid-button">Bid</button>
	  <button id="short-button">Short</button>
	  <button id="exchange-button">Exchange</button>
	</div>
  `;
  document.body.appendChild(stockTradeModal);

  // Generate unique ID for tracking
  const modalId = `stockTradeModal_${Date.now()}`;
  stockTradeModal.id = modalId;

  // Track popup state for bot interaction management
  if (window.popupTracker) {
    window.popupTracker.trackElement(stockTradeModal, 'stock-trade-modal');
  }

  const closeButton = stockTradeModal.querySelector('.close');
  closeButton.onclick = () => {
	// Untrack popup before removing
	if (window.popupTracker) {
	  window.popupTracker.unregisterPopup(modalId);
	}
	stockTradeModal.remove();
  };

  const bidButton = stockTradeModal.querySelector('#bid-button');
  bidButton.onclick = () => {
	localStorage.setItem('stockData', JSON.stringify(stock));
	window.location.href = `stockTransaction.html?stockId=${stock._id}&stockName=${stock.name}`;
  };

  const exchangeButton = stockTradeModal.querySelector('#exchange-button');
  exchangeButton.onclick = () => {
	const itemId = prompt(t('stock.enter_item_id', 'Enter the item ID to exchange for the stock:'));
	if (!itemId) return;
  
	const s = (typeof window !== 'undefined') ? window.popupTrackerSocket : null;
	if (!s || !s.connected) {
	  showNotification(t('stock.ws_not_connected', 'Not connected. Please try again.'), false);
	  return;
	}
  
	const payload = {
	  stockId: stock._id,
	  itemId,
	  portfolioId: getPortfolioId(),
	  portfolioName: getCookie('username'),
	  sessionId: getSessionId()
	};
  
	s.emit('exchangeStockForItem', payload, (ack) => {
	  if (ack && ack.success === false) {
		showNotification(ack.error || t('stock.exchange_failed', 'Exchange failed.'), false);
		return;
	  }
	  showNotification(t('stock.exchange_sent', 'Exchange proposed.'), true);
	  stockTradeModal.remove();
	});
  };
}

// Function to show stock bid notification popup
// 20s to answer (it was 10 — not enough to read who wants what; the server
// keeps the bid open 24s). Keep the two in step: socketHandlers.js placeBid.
export function showStockBidPopup(bidData, portfolioId, timeoutDuration = 20000) {
  console.log('Attempting to show stock bid popup:', bidData);
  bidData.isResponder = true;
  // Remove any existing popup
  let existingPopup = document.getElementById("stockBidPopup");
  if (existingPopup) {
	console.log('Removing existing popup.');
	existingPopup.remove();
  }

  // Create a new popup
  const popup = document.createElement("div");
  popup.setAttribute("id", "stockBidPopup");
  popup.setAttribute("style", `
	position: fixed;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	width: 100%;
	max-width: 100%;
	height: 100%;
	max-height: 100%;
	border-radius: 0px;
	box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
	z-index: 9999;
	font-family: 'bithigh', Arial, sans-serif;
	color: #00ff00;
	text-align: center;
	align-content: center;
	overflow: hidden;
  `);

  // Create video element
  const video = document.createElement("video");
  video.setAttribute("id", "stockBidBackground");
  video.setAttribute("style", `
	  position: absolute;
	  top: 0;
	  left: 0;
	  width: 100%;
	  height: 100%;
	  object-fit: cover;
	  z-index: -1;
	  pointer-events: none; /* Ensure the video is non-interactive */
	`);
  video.setAttribute("autoplay", "true");
  video.setAttribute("loop", "true");
  video.setAttribute("muted", "true");
  video.setAttribute("playsinline", "true"); // Required for autoplay on mobile
  popup.appendChild(video);

  // Prevent interaction with the video
  video.addEventListener('click', (event) => {
	event.stopPropagation();
	event.preventDefault();
  });

  // Prevent fullscreen on double-click
  video.addEventListener('dblclick', (event) => {
	event.stopPropagation();
	event.preventDefault();
  });

  // Prevent right-click context menu
  video.addEventListener('contextmenu', (event) => {
	event.preventDefault();
  });

  // Create content container
  const content = document.createElement("div");
  content.setAttribute("style", `
	 position: relative;
	 z-index: 1;
	 padding: 22px 18px;
	 margin: 0 16px;
	 border-radius: 10px;
	 background: rgba(0, 0, 0, 0.6);
	 text-shadow: 0 1px 3px #000;
  `);
 
  // Translate company name using companies_by_id pattern if csvRow is available
  const csvRow = bidData.csvRow || bidData.companyCsvId || bidData.stockCsvRow;
  const translatedStockName = csvRow
	? t(`companies_by_id.${csvRow}.Company_Name`, bidData.stockName)
	: bidData.stockName;

  // What they want and what they pay — nothing else. Tester: "too much text
  // on the stock bid from another trader, I can't read it all; focus on the
  // stock and the bid value." The explainer paragraph and the full sentence
  // are gone; who is asking is a small line above.
  const escH = v => String(v == null ? '' : v).replace(/[&<>"']/g, c =>
	({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  let offerHtml = '';
  if (bidData.bidType === 'zollars') {
	const parts = Object.entries(bidData.zollarBid || {})
	  .filter(([, v]) => v > 0)
	  .map(([type, v]) => `${escH(type.charAt(0).toUpperCase() + type.slice(1))} <span style="opacity:.75">(${escH(getQualitativeDescriptor(v))})</span>`);
	offerHtml = parts.length ? parts.join('<br>') : escH(t('stock_bid.zollars', 'zollars'));
  } else {
	offerHtml = `Ƶ${escH(bidData.amount)}`;
  }
  const big = 'font-size: clamp(1.6rem, 7vw, 2.6rem); line-height: 1.15; margin: 6px 0;';

  content.innerHTML = `
	<div style="font-size: clamp(0.95rem, 3.6vw, 1.15rem); opacity: .85;">${escH(t('stock_bid.wants_to_buy', '{portfolioName} wants to buy').replace('{portfolioName}', bidData.portfolioName || ''))}</div>
	<div style="${big} color:#fff;">${escH(translatedStockName)}${Number(bidData.shares) > 1 ? ` <span style="opacity:.7">×${escH(bidData.shares)}</span>` : ''}</div>
	<div style="font-size: clamp(0.95rem, 3.6vw, 1.15rem); opacity: .85; margin-top: 14px;">${escH(t('stock_bid.offering', 'offering'))}</div>
	<div style="${big} color:#fcba03;">${offerHtml}</div>
	<div style="margin-top: 18px;">
	  <button id="acceptBid" style="margin: 10px; padding: 14px 28px; min-height: 48px; min-width: 120px; background-color: rgba(0, 255, 0, 0.8); color: black; border: none; cursor: pointer; touch-action: manipulation; font-size: 18px; font-family: 'bithigh', Arial, sans-serif;">${escH(t('stock_bid.accept', 'Accept'))}</button>
	  <button id="declineBid" style="margin: 10px; padding: 14px 28px; min-height: 48px; min-width: 120px; background-color: rgba(255, 0, 0, 0.75); color: white; border: none; cursor: pointer; touch-action: manipulation; font-size: 18px; font-family: 'bithigh', Arial, sans-serif;">${escH(t('stock_bid.decline', 'Decline'))}</button>
	</div>
	<div id="bidTimer" style="opacity:.7"></div>
  `;
  popup.appendChild(content);
 
  // Append the popup to the body
  document.body.appendChild(popup);
  document.body.style.overflow = 'hidden';
  console.log('Popup should now be visible.');

  // Track popup state for bot interaction management
  if (window.popupTracker) {
    window.popupTracker.trackElement(popup, 'stock-bid-popup');
  }


  // Video background logic
  const videoBaseUrl = `${apiBaseUrl}/uploads/specialBacks/stockBack/`;
  const totalVideos = 62;
  const videoIndex = Math.floor(Math.random() * totalVideos) + 1;
  const videoUrl = `${videoBaseUrl}${videoIndex}/playlist.m3u8`;

  if (Hls.isSupported()) {
	const hls = new Hls();
	hls.loadSource(videoUrl);
	hls.attachMedia(video);
	hls.on(Hls.Events.MANIFEST_PARSED, function() {
	  video.play();
	});
  } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
	video.src = videoUrl;
	video.addEventListener('loadedmetadata', function() {
	  video.play();
	});
  }

  const timerElement = document.getElementById('bidTimer');

  let remainingTime = timeoutDuration / 1000;
  const timerInterval = setInterval(() => {
	remainingTime -= 1;
	if (timerElement) {
	  timerElement.textContent = t('stock_bid.time_remaining', 'Time remaining: {seconds} seconds').replace('{seconds}', remainingTime);
	}
	if (remainingTime <= 0) {
	  clearInterval(timerInterval);
	  if (timerElement) {
		timerElement.textContent = t('stock_bid.time_expired', 'Time expired');
	  }
	  // Nobody answered: the floor asks the room to make the offer again if a
	  // goal was waiting on it.
	  try { document.dispatchEvent(new CustomEvent('zx:incomingBidExpired')); } catch (_) {}
	  // Not answering is not declining. This used to send a refusal in the
	  // participant's name — "You declined the bid." and a market comment on
	  // a trade that never happened, for an offer they may not even have seen.
	  // The offer just closes; the server lets the bid lapse by itself (24s).
	  try { popup.remove(); } catch (_) {}
	  document.body.style.overflow = 'auto';
	}
  }, 1000);

 // Accept bid function
 const acceptBid = () => {
   clearInterval(timerInterval);

   const stockId = bidData.stockId;
   const bidId = bidData.bidId;
   const transactionId = bidData.transactionId || bidData.bidId;
   const initiatorPortfolioId = bidData.bidderId || bidData.initiatorPortfolioId;
   const responderPortfolioId = getPortfolioId();
   const bidShares = bidData.shares;
   const bidType = bidData.bidType;
   const token = getAuthToken();
   const sessionId = getSessionId();

   if (!(stockId && bidId && initiatorPortfolioId && responderPortfolioId && bidShares && bidType && token)) {
	 console.error('Invalid data for accepting bid:', { stockId, bidId, transactionId, initiatorPortfolioId, responderPortfolioId, bidShares, bidType });
	 return;
   }

   // Mark user as responder - prevents redirect even if on stockTransaction.html
   window.__userIsInitiator = false;

   const _onceMood = (payload) => {
	 try {
	   if (payload && payload.success !== false) {
		 const mood = payload.analysis || payload.moodResult;
		 if (mood) {
		   // Show immediately for responder (don't store, don't set redirect flag)
		   try {
			 showMoodAnalysisPopup(mood);
			 // Clear sessionStorage to prevent duplicate display on next page
			 sessionStorage.removeItem('pendingMoodAnalysis');
			 // DO NOT set window.__pendingMarketRedirect for responders
		   } catch(e) {
			 console.error('Failed to show mood popup:', e);
		   }
		 }
	   }
	 } finally {
	   socket.off('moodAnalysis', _onceMood);
	   // DO NOT redirect for responders
	 }
   };
   socket.once('moodAnalysis', _onceMood);

   // REMOVED duplicate notification - socketU.js bidAcceptSuccess handler will show it
   // This prevents double notifications when accepting a bid

   if (!socket || !socket.connected) {
	 showNotification(t('stock.ws_not_connected', 'Not connected. Please try again.'), false);
	 socket.off('moodAnalysis', _onceMood); // cleanup if we bail
	 return;
   }

   try { popup.remove(); } catch(e){}
   document.body.style.overflow = 'auto';

   socket.emit('acceptBid', {
	 transactionId,
	 stockId,
	 bidId,
	 portfolioId: responderPortfolioId, // seller
	 bidderId:   initiatorPortfolioId,  // buyer
	 bidShares,
	 bidType,
	 token,
	 sessionId
   }, (ack) => {
	 if (ack && ack.success === false) {
	   showNotification(ack.error || t('stock.accept_failed', 'Failed to accept bid.'), false);
	   return;
	 }
	 // Success notification will be shown by bidAcceptSuccess socket handler
   });
 };

 const declineBid = () => {
   clearInterval(timerInterval);

   const stockId = bidData.stockId;
   const bidId = bidData.bidId;
   const transactionId = bidData.transactionId || bidData.bidId;
   const initiatorPortfolioId = bidData.bidderId || bidData.initiatorPortfolioId; // buyer
   const responderPortfolioId = getPortfolioId(); // seller
   const bidShares = bidData.shares;
   const bidType = bidData.bidType;
   const token = getAuthToken();
   const sessionId = getSessionId();

   if (!(stockId && bidId && initiatorPortfolioId && responderPortfolioId && bidShares && bidType && token)) {
	 console.error('Invalid data for declining bid:', { stockId, bidId, transactionId, initiatorPortfolioId, responderPortfolioId, bidShares, bidType });
	 return;
   }

   // Mark user as responder - prevents redirect even if on stockTransaction.html
   window.__userIsInitiator = false;

   const _onceMood = (payload) => {
	 try {
	   if (payload && payload.success !== false) {
		 const mood = payload.analysis || payload.moodResult;
		 if (mood) {
		   // Show immediately for responder (don't store, don't set redirect flag)
		   try {
			 showMoodAnalysisPopup(mood);
			 // Clear sessionStorage to prevent duplicate display on next page
			 sessionStorage.removeItem('pendingMoodAnalysis');
			 // DO NOT set window.__pendingMarketRedirect for responders
		   } catch(e) {
			 console.error('Failed to show mood popup:', e);
		   }
		 }
	   }
	 } finally {
	   socket.off('moodAnalysis', _onceMood);
	   // DO NOT redirect for responders
	 }
   };
   socket.once('moodAnalysis', _onceMood);

   // REMOVED duplicate notification - socketU.js bidDeclineSuccess handler will show it
   // This prevents double notifications when declining a bid

   if (!socket || !socket.connected) {
	 showNotification(t('stock.ws_not_connected', 'Not connected. Please try again.'), false);
	 socket.off('moodAnalysis', _onceMood); // cleanup if we bail
	 return;
   }

   try { popup.remove(); } catch(e){}
   document.body.style.overflow = 'auto';

   socket.emit('declineBid', {
	 transactionId,
	 stockId,
	 bidId,
	 portfolioId: responderPortfolioId, // seller
	 bidderId:   initiatorPortfolioId,  // buyer
	 bidShares,
	 bidType,
	 token,
	 sessionId
   }, (ack) => {
	 if (ack && ack.success === false) {
	   showNotification(ack.error || t('stock.decline_failed', 'Failed to decline bid.'), false);
	   return;
	 }
	 // Success notification will be shown by bidDeclineSuccess socket handler
   });
 };

  const acceptButton = popup.querySelector("#acceptBid");
  const declineButton = popup.querySelector("#declineBid");
   
  if (acceptButton && declineButton) {
	// The offer covers the whole screen the instant it arrives. A finger
	// already on its way to something else landed on ACCEPT or DECLINE and
	// answered an offer nobody had read (tester: "I touched a button … and it
	// killed the offer"). The buttons wake up after a moment.
	[acceptButton, declineButton].forEach(b => { b.disabled = true; b.style.opacity = '0.35'; });
	setTimeout(() => [acceptButton, declineButton].forEach(b => { b.disabled = false; b.style.opacity = ''; }), 1200);
	acceptButton.addEventListener('click', acceptBid);
	declineButton.addEventListener('click', () => {
	  // A pressed refusal counts on the floor as an answer (SELLABLE HOURS ·
	  // DILEMMA); letting the timer run out does not.
	  try { document.dispatchEvent(new CustomEvent('zx:incomingBidDeclined')); } catch (_) {}
	  declineBid();
	});
  } else {
	console.error("Accept or decline buttons not found in DOM.");
  }

  function displayZollarDetails(zollarBid) {
	if (!zollarBid || Object.keys(zollarBid).length === 0) {
	  return '<p>No zollar details available.</p>';
	}
   
	let zollarHtml = '<ul>';
	for (const [type, value] of Object.entries(zollarBid)) {
	  if (value > 0) { // Only show zollars with a non-zero value
		const descriptor = getQualitativeDescriptor(value);
		zollarHtml += `<li>${type}: ${descriptor}</li>`;
	  }
	}
	zollarHtml += '</ul>';
	return zollarHtml;
  }
}
 
export function closeWaitingForResponsePopup(isResponder = false) {
  console.log('closeWaitingForResponsePopup called, isResponder:', isResponder);

  // An answer to MY OWN bid closes my waiting screen — it must not also throw
  // away an offer someone else has just made me and I have not answered yet.
  const ownWait = !!(document.getElementById('waitingPopup') || document.getElementById('bidSubmittingOverlay'));
  const ids = ownWait ? ['waitingPopup', 'bidSubmittingOverlay'] : ['waitingPopup', 'stockBidPopup', 'bidSubmittingOverlay'];
  for (const id of ids) {
	const el = document.getElementById(id);
	if (el) {
	  // Untrack popup before removing
	  if (window.popupTracker) {
		window.popupTracker.unregisterPopup(id);
	  }
	  el.remove();
	}
  }
  
  // Clear all intervals
  if (window.__bidSubmittingInterval) {
	clearInterval(window.__bidSubmittingInterval);
	window.__bidSubmittingInterval = null;
  }
  if (window.__instantWaitInterval) {
	clearInterval(window.__instantWaitInterval);
	window.__instantWaitInterval = null;
  }
  if (window.__waitingPopupInterval) {
	clearInterval(window.__waitingPopupInterval);
	window.__waitingPopupInterval = null;
  }
  
  try { document.body.style.overflow = ''; } catch(_) {}
  
  // CRITICAL: If responder, do nothing and exit
  if (isResponder) {
	console.log('Responder - NOT redirecting');
	return;
  }
  
  console.log('Initiator - setting up redirect logic');
  
  // Only for initiators: check if mood popup is open
  try {
	const moodEl =
	  document.getElementById('moodAnalysisPopup') ||
	  document.getElementById('moodPopup') ||
	  document.querySelector('.mood-popup, .moodAnalysis, .mood-analysis, [data-mood-popup]');
	
	if (moodEl) {
	  window.__pendingMarketRedirect = true;
	  const obs = new MutationObserver(() => {
		const stillOpen =
		  document.getElementById('moodAnalysisPopup') ||
		  document.getElementById('moodPopup') ||
		  document.querySelector('.mood-popup, .moodAnalysis, .mood-analysis, [data-mood-popup]');
		if (!stillOpen) {
		  try { doMarketRedirectIfNeeded(); } catch (_) {}
		  obs.disconnect();
		}
	  });
	  obs.observe(document.body, { childList: true, subtree: true });
	} else {
	  doMarketRedirectIfNeeded();
	}
  } catch (e) {
	try { doMarketRedirectIfNeeded(); } catch (_) {}
  }
}

 
async function addGlobalIndicator(stockElement, stock) {
  if (!stock.isSystemCompany || !stock.systemCompanyId) return;
  
  try {
	const response = await fetch(`${apiBaseUrl}/api/stocks/global-valuation/${stock.systemCompanyId}`, {
	  headers: { 'Authorization': `Bearer ${getAuthToken()}` }
	});
	
	if (response.ok) {
	  const globalData = await response.json();
	  const indicator = document.createElement('small');
	  indicator.style.color = '#00ff00';
	  indicator.style.display = 'block';
	  indicator.innerHTML = `🌐 Global: Ƶ${globalData.currentShareValue.toFixed(2)} | Trades: ${globalData.tradingStats.totalTransactions}`;
	  stockElement.querySelector('h3').after(indicator);
	}
  } catch (error) {
	console.warn('Could not add global indicator:', error.message);
  }
}
 
function getQualitativeDescriptor(quantity) {
   if (quantity === 0) {
	 return 'None';
   } else if (quantity >= 1 && quantity <= 3) {
	 return 'A Few';
   } else if (quantity >= 4 && quantity <= 7) {
	 return 'Some';
   } else if (quantity >= 8 && quantity <= 15) {
	 return 'Many';
   } else {
	 return 'A Huge Amount';
   }
 }

function setFallbackBackground(element) {
  element.style.backgroundImage = 'linear-gradient(45deg, #000000, #001f3f)';
  element.style.backgroundSize = 'cover';
}